// src/components/sections/CabinetComparisonSection.tsx
//
// Section comparative compacte "Cabinet classique vs recruteurs.io" pour la home.
// Reprend 5 des 8 axes de la page /pourquoi-recruteurs-io (les plus
// impactants visuellement / commercialement), avec un CTA vers la page
// complète pour le visiteur qui veut creuser.

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, Eye, ShieldCheck, Coins, Brain, TrendingDown } from 'lucide-react';
import Section from '../ui/section';
import SectionBackground from './section-background';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

interface CompareRow {
  axis: string;
  icon: React.ComponentType<{ className?: string }>;
  classic: string;
  io: string;
}

const ROWS: CompareRow[] = [
  {
    axis: 'Expérience candidat avant signature',
    icon: Eye,
    classic: 'Deux entretiens, le candidat signe sans avoir vu la réalité.',
    io: 'Plateforme Parcours Découverte propriétaire — vidéos équipe, grille rémunération, journée type.',
  },
  {
    axis: 'Garantie en cas de rupture',
    icon: ShieldCheck,
    classic: 'Garantie 3 mois floue, conditions opaques.',
    io: 'Garantie 6 mois opposable : replacement gratuit OU 30 % du fee restitué.',
  },
  {
    axis: 'Transparence tarifaire',
    icon: Coins,
    classic: 'Tarifs cachés, négociation cas par cas.',
    io: 'Tarifs publics sur le site. Chiffrage transparent dès le premier appel.',
  },
  {
    axis: 'IA appliquée au recrutement',
    icon: Brain,
    classic: 'Aucune, ou un slide marketing.',
    io: "Outils maison (Mapping RH, scoring CV, génératif marque employeur). Précurseurs depuis 2022.",
  },
  {
    axis: 'No-show post-signature',
    icon: TrendingDown,
    classic: '1 candidat sur 5 ne se présente pas le jour J.',
    io: '−50 % de no-show observé chez nos clients grâce au Parcours Découverte.',
  },
];

export default function CabinetComparisonSection() {
  const navigate = useNavigate();

  return (
    <SectionBackground page="home" section="home-comparison" className="bg-white">
      <Section>
        <div className="max-w-4xl mx-auto text-center mb-12">
          <span className={eyebrowClass}>Cabinet classique vs cabinet augmenté</span>
          <h2 className={h2Class}>
            Pourquoi nous, plutôt qu'un{' '}
            <span className={gradientText}>cabinet classique.</span>
          </h2>
          <p className={sectionSubtitleClass}>
            Cinq axes structurels où nous nous distinguons. Pas du marketing —
            du contractuel, du mesurable, du chiffré.
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-3">
          {ROWS.map((row, idx) => {
            const Icon = row.icon;
            return (
              <motion.div
                key={row.axis}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: idx * 0.05 }}
                className="rounded-2xl bg-white ring-1 ring-neutral-200 overflow-hidden shadow-sm"
              >
                {/* Axe header */}
                <div className="flex items-center gap-3 px-5 md:px-6 py-3 bg-[#fbfaf8] border-b border-neutral-100">
                  <Icon className="w-4 h-4 text-[#d03484] flex-shrink-0" />
                  <h3 className="text-xs md:text-sm font-bold text-[#30475e] uppercase tracking-wider">
                    {row.axis}
                  </h3>
                </div>
                {/* Comparaison côte à côte */}
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Cabinet classique */}
                  <div className="p-4 md:p-5 border-b md:border-b-0 md:border-r border-neutral-100 bg-neutral-50/50">
                    <div className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-neutral-200">
                        <X className="w-3 h-3 text-neutral-500" />
                      </span>
                      <div>
                        <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest mb-1">
                          Cabinet classique
                        </p>
                        <p className="text-sm text-neutral-600 leading-relaxed">
                          {row.classic}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* recruteurs.io */}
                  <div className="p-4 md:p-5 bg-gradient-to-br from-[#fde7f2]/30 to-[#fde2d3]/30">
                    <div className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] shadow-sm">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1">
                          <span className={gradientText}>recruteurs.io</span>
                        </p>
                        <p className="text-sm text-[#30475e] leading-relaxed">
                          {row.io}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA vers la page comparative complète */}
        <div className="max-w-2xl mx-auto text-center mt-10">
          <p className="text-sm text-neutral-500 mb-4">
            Encore 3 axes : méthode de cadrage, ancrage local PACA, suivi 90 jours chiffré.
          </p>
          <button
            onClick={() => navigate('/pourquoi-recruteurs-io')}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
          >
            Voir le comparatif complet (8 axes)
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </Section>
    </SectionBackground>
  );
}
